import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// VW T1 Bus (Samba microbus) — a one-box forward-control classic at true
// scale (1 unit = 1 metre): length ~4.28, width 1.72, height 1.94 (taller
// than it is wide, as the real bus is), wheelbase 2.40 on small 0.30 wheels.
// Nose is +Z; the flat front sits directly over the front axle (no bonnet).
// Signature cues: two-tone paint split at a chrome belt strip — sealing-wax
// red below (the "paint" slot), cream above — with the cream V descending
// the nose between the wide-set round headlamps, a big plain chrome badge
// disc on the V, a split windscreen (two panes with a centre divider) on a
// slightly raked cab face, a row of square side windows proud of the cream
// band, an engine lid with cooling louvres and low tail lamps at the rear,
// slim chrome bumpers, steel wheels with whitewall rings and domed chrome
// hubcaps, and small round mirrors on short arms at the A-pillars.

const PAINT = "#b8362a"; // sealing-wax red lower body — the "paint" slot default
const CREAM = "#efe8d8"; // cream upper body and roof (unslotted two-tone half)
const TRIM = "#17181b"; // underbody, axles, wipers
const WELL = "#141518"; // wheel-well discs
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen panes (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SILVER = "#adb3ba"; // steel wheel discs
const TIRE = "#1a1b1d";
const WALL = "#d8d4c6"; // whitewall hint ring on the tyre face
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527";
const PLATE = "#eceff2";
const LOUVRE = "#3f1712"; // dark cooling slots cut into the red engine lid

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.3;
const WHEEL_X = 0.8; // half track — tyre face proud of the well disc
const AXLE_F = 1.2;
const AXLE_R = -1.2; // wheelbase 2.40, axles symmetric under the box
const RAKE = -0.1; // slight windscreen / cab-face rake, top toward the tail

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A cream upper-body panel — the fixed half of the two-tone scheme. */
function cream(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, CREAM, position, rotation);
}

/** One classic steel wheel: tyre, thin whitewall ring proud of its face,
 *  silver steel disc, chrome hubcap base and a flattened chrome dome, with a
 *  dark brake drum inboard. Every part shares `name` so the `drive` track
 *  spins them as one node about the axle. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  return [
    cyl(WHEEL_R, WHEEL_R, 0.2, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.225, 0.225, 0.02, 18, WALL, [x + side * 0.095, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.16, 0.16, 0.04, 16, SILVER, [x + side * 0.1, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.09, 0.09, 0.035, 14, CHROME, [x + side * 0.125, y, z], [0, 0, Math.PI / 2], { name }),
    {
      geometry: new THREE.SphereGeometry(0.065, 12, 8),
      color: CHROME,
      position: [x + side * 0.13, y, z] as Vec3,
      scale: [0.5, 1, 1] as Vec3,
      name,
    },
    cyl(0.14, 0.14, 0.06, 12, TRIM, [x - side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
  ];
}

/** The opened wheel well: a dark disc proud of the body side (the arch cut)
 *  plus a painted half-torus lip over the top — the T1 has body-colour
 *  fender lips, not black cladding. The [0, pi/2, 0] yaw puts the torus in
 *  the YZ plane with its 0..pi arc sweeping exactly the top half. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.39, 0.39, 0.03, 18, WELL, [side * 0.865, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.36, 0.04, 6, 18, Math.PI),
      color: PAINT,
      position: [side * 0.87, WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the
// parts. Fronts sit level with the headlamps at the nose corners, rears sit
// inboard of the low tail lamps, kept nearly flush with the tail face.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.78, 1.0, 2.14], size: [0.07, 0.06, 0.03] },
  { name: "indicator-fr", pos: [0.78, 1.0, 2.14], size: [0.07, 0.06, 0.03] },
  { name: "indicator-rl", pos: [-0.6, 0.78, -2.121], size: [0.08, 0.08, 0.03] },
  { name: "indicator-rr", pos: [0.6, 0.78, -2.121], size: [0.08, 0.08, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.5, 0.1, 4.2, TRIM, [0, 0.31, 0])); // underbody pan — fills to the wells, raised so tyres read from dead ahead

  // -- One-box body masses: red below the belt, cream above --------------------
  P.push(paint(1.72, 0.74, 4.24, [0, 0.67, 0])); // lower body, sill to belt line
  P.push(box(1.74, 0.025, 4.18, CHROME, [0, 1.045, 0])); // chrome belt strip at the two-tone split
  P.push(cream(1.72, 0.62, 4.04, [0, 1.35, -0.1])); // upper band, window line, stops short of the cab face
  P.push(cream(1.73, 0.64, 0.2, [0, 1.34, 2.0], [RAKE, 0, 0])); // slightly raked cab face over the front axle
  // One smooth domed roof: a z-axis barrel with an elliptical cross-section
  // plus half-ellipsoid caps whose x/y semi-axes match the barrel's exactly,
  // so crown, front curve and rear curve read as a single surface. The 0.84
  // half-width leaves a subtle 0.02 rain-gutter ledge on the 0.86 band top.
  // Axis-aligned 90-degree rotation with per-axis scale is shear-free.
  P.push({
    geometry: new THREE.CylinderGeometry(1, 1, 1, 24),
    color: CREAM,
    position: [0, 1.66, -0.175] as Vec3,
    rotation: [Math.PI / 2, 0, 0] as Vec3,
    scale: [0.84, 3.55, 0.28] as Vec3,
  });
  P.push({
    geometry: new THREE.SphereGeometry(1, 24, 16),
    color: CREAM,
    position: [0, 1.66, 1.6] as Vec3,
    scale: [0.84, 0.28, 0.45] as Vec3,
  });
  P.push({
    geometry: new THREE.SphereGeometry(1, 24, 16),
    color: CREAM,
    position: [0, 1.66, -1.95] as Vec3,
    scale: [0.84, 0.28, 0.17] as Vec3,
  });

  // -- Split windscreen on the raked cab face ----------------------------------
  for (const s of S) P.push(box(0.66, 0.42, 0.04, SCREEN, [s * 0.37, 1.34, 2.121], [RAKE, 0, 0]));
  P.push(cream(0.09, 0.46, 0.05, [0, 1.34, 2.125], [RAKE, 0, 0])); // centre divider pillar, proud of both panes
  for (const s of S) P.push(box(0.02, 0.24, 0.02, TRIM, [s * 0.32, 1.21, 2.166], [RAKE, 0, s * 0.3])); // wipers

  // -- Two-tone V on the nose: cream descends between the headlamps ------------
  // Two tilted arms meet at a low apex; a rectangle fills the region between
  // their upper ends up to the belt. Each plane sits at its own tiny z offset
  // so the overlapping cream panels never z-fight.
  P.push(cream(0.58, 0.2, 0.022, [-0.22, 0.875, 2.129], [0, 0, -0.672]));
  P.push(cream(0.58, 0.2, 0.022, [0.22, 0.875, 2.131], [0, 0, 0.672]));
  P.push(cream(0.56, 0.25, 0.02, [0, 0.9275, 2.127])); // fill between the arms up to the belt
  P.push(cream(0.12, 0.12, 0.02, [0, 0.66, 2.1275], [0, 0, Math.PI / 4])); // diamond sharpening the V point
  P.push(cyl(0.13, 0.13, 0.018, 20, CHROME, [0, 0.8, 2.152], [Math.PI / 2, 0, 0])); // plain badge disc, centred mid-nose in the V

  // -- Front fascia -------------------------------------------------------------
  for (const s of S) {
    P.push(cyl(0.105, 0.105, 0.03, 16, CHROME, [s * 0.62, 1.0, 2.135], [Math.PI / 2, 0, 0])); // headlamp bezel
    P.push(cyl(0.082, 0.082, 0.02, 14, LAMP, [s * 0.62, 1.0, 2.148], [Math.PI / 2, 0, 0])); // headlamp glass
  }
  P.push(box(1.64, 0.1, 0.08, CHROME, [0, 0.42, 2.155])); // slim chrome bumper, rear face sunk into the nose
  P.push(box(0.36, 0.09, 0.02, PLATE, [0, 0.525, 2.135])); // plain front plate between bumper and V apex

  // -- Side glazing: door pane + a row of square windows proud of the band -----
  for (const s of S) {
    P.push(box(0.03, 0.34, 0.62, GLASS, [s * 0.87, 1.31, 1.62])); // cab door glass, tight behind a thin A-pillar
    for (const z of [0.86, 0.11, -0.64, -1.39]) {
      P.push(box(0.03, 0.34, 0.6, GLASS, [s * 0.87, 1.31, z])); // square window row
    }
    P.push(box(0.02, 0.025, 0.12, CHROME, [s * 0.87, 0.92, 1.3])); // cab door handle
    P.push(box(0.02, 0.025, 0.12, CHROME, [s * 0.87, 0.92, 0.15])); // side cargo-door handle
  }

  // -- Rear fascia: every fitting nearly flush so only the bumper breaks the
  // tail silhouette in profile (each layer 0.008-0.012 proud of its backing) --
  P.push(box(0.9, 0.34, 0.03, GLASS, [0, 1.3, -2.113])); // small rear window in the cream band
  P.push(paint(1.05, 0.44, 0.025, [0, 0.7, -2.1175])); // engine lid, barely proud of the tail
  for (let i = 0; i < 4; i++) {
    P.push(box(0.68, 0.028, 0.012, LOUVRE, [0, 0.84 - i * 0.075, -2.132])); // cooling louvres on the lid
  }
  P.push(box(0.1, 0.025, 0.03, CHROME, [0, 0.9, -2.125])); // lid handle
  for (const s of S) {
    P.push(box(0.13, 0.19, 0.02, CHROME, [s * 0.72, 0.78, -2.118])); // tail-lamp bezel plate
    P.push(box(0.1, 0.16, 0.03, RED, [s * 0.72, 0.78, -2.121])); // low tail lamp, proud of its bezel
  }
  P.push(box(1.64, 0.1, 0.08, CHROME, [0, 0.42, -2.155])); // slim chrome rear bumper
  P.push(box(0.32, 0.09, 0.02, PLATE, [0, 0.545, -2.132])); // plain rear plate on the lid

  // -- Furniture -------------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.11, 0.03, 0.03, CHROME, [s * 0.9, 1.34, 1.98])); // stout mirror arm on the A-pillar
    P.push(cyl(0.05, 0.05, 0.02, 12, CHROME, [s * 0.945, 1.34, 1.98], [Math.PI / 2, 0, 0])); // round mirror head
    P.push(cyl(0.038, 0.038, 0.01, 12, TRIM, [s * 0.945, 1.34, 1.965], [Math.PI / 2, 0, 0])); // mirror face
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the bus toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const vwT1Bus = defineModel({
  id: "vw-t1-bus",
  name: "VW T1 Bus",
  batchId: "cars",
  tags: ["car", "volkswagen", "t1", "bus", "van", "microbus", "classic"],
  summary: "A two-tone VW T1 Samba microbus at true scale, sealing-wax red over cream.",
  description:
    "A Volkswagen T1 Bus (Samba microbus) at real 1 unit = 1 metre scale " +
    "(about 4.28 long, 1.72 wide, 1.94 tall — taller than it is wide — on a " +
    "2.40 wheelbase with the flat front directly over the front axle). " +
    "One-box forward-control body with the signature two-tone split at a " +
    "chrome belt strip: sealing-wax red below, cream above, the cream " +
    "descending the nose in a V between the wide-set round chrome-bezel " +
    "headlamps, with a big plain chrome badge disc centred on the V. Split " +
    "windscreen (two panes and a centre divider) on a slightly raked cab " +
    "face under a rounded roof front, a row of five square windows proud of " +
    "the cream band per side, small round mirrors on short A-pillar arms, " +
    "slim chrome bumpers, and a rear engine lid with cooling louvres, low " +
    "tail lamps and a small rear window. Steel wheels wear whitewall rings, " +
    "silver discs and domed chrome hubcaps. The red panels carry the " +
    "'paint' recolour slot; `drive` spins the wheels and `hazards` blinks " +
    "the four indicators.",
  author: "Fable 5",
  footprint: 2.3,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
